<template>
    <CompassTableRow :class="tableRowClass" :checkbox="checkboxValues">
        <template v-for="column in columns">
            <!-- PARTICIPANTS -->
            <CompassTableBodyCell
                v-if="column.code === 'participants'"
                :key="column.code"
                maxWidth="256px"
            >
                <div class="email-listing-table__cell-participants">
                    <span v-if="isReceivedTable" class="email-listing-table__state-dot" />
                    <span
                        class="email-listing-table__cell-participants-label typographies-paragraph-medium-semibold"
                    >
                        {{ formattedParticipants }}
                    </span>
                </div>
            </CompassTableBodyCell>

            <!-- Subject & Related -->
            <CompassTableBodyCell
                v-if="column.code === 'subject'"
                :key="column.code"
                maxWidth="506px"
                class="email-listing-table__cell-subject"
            >
                <div class="email-listing-table__cell-subject-container">
                    <div class="email-listing-table__cell-subject-content">
                        {{ email.subject }}
                    </div>
                    <CompassIcon
                        v-if="email.has_attachments"
                        class="email-listing-table__cell-subject-attachment"
                        icon="attachment"
                        size="small"
                    />
                </div>
            </CompassTableBodyCell>

            <!-- related -->
            <CompassTableBodyCell
                v-if="column.code === 'related'"
                :key="column.code"
                class="email-listing-table__cell-related"
            >
                <CompassButton
                    v-if="email.related.length > 0"
                    type="tertiary"
                    leftIcon="objects-linked"
                    :label="`${email.related.length}`"
                    @click="openRelated(email)"
                />
            </CompassTableBodyCell>

            <!-- Status -->
            <CompassTableBodyCell
                v-if="column.code === 'status'"
                :key="column.code"
                maxWidth="142px"
            >
                <CompassTag v-if="isSentTable && email.is_open" :color="EMAIL_STATUS.OPEN.tagColor">
                    {{ EMAIL_STATUS.OPEN.label }}
                </CompassTag>

                <CompassTag v-else-if="!isSentTable" :color="getStatusColorTag()">
                    {{ getStatusLabel() }}
                </CompassTag>
            </CompassTableBodyCell>

            <!-- DATE -->
            <CompassTableBodyCell
                v-if="column.code === 'date'"
                :key="column.code"
                maxWidth="175px"
                class="email-listing-table__cell-date"
            >
                {{ formattedDate(email.date) }}
            </CompassTableBodyCell>

            <CompassTableBodyCell
                v-if="column.code === 'updated'"
                :key="column.code"
                maxWidth="175px"
            >
                {{ formattedDate(email.updated) }}
            </CompassTableBodyCell>

            <!-- Scheduled DATE -->
            <CompassTableBodyCell
                v-if="column.code === 'scheduled_at'"
                :key="column.code"
                maxWidth="225px"
            >
                {{ $t.__('email.ScheduledEmailListing.scheduled_date') }} :
                {{ formattedDate(email.scheduled_at) }}
            </CompassTableBodyCell>
        </template>
        <CompassTableBodyCell ref="cell-actions" fixed="right" maxWidth="64px" minWidth="64px">
            <EmailListingActions
                ref="wrapper-icon-buttons"
                class="actions__container"
                :email="email"
                @update:readingStatus="updateReadingStatus"
                @open:related="openRelated"
            />
        </CompassTableBodyCell>
    </CompassTableRow>
</template>
<script>
import EmailsListingMixins from 'mixins/emails/EmailsListingMixins';
import EmailListingActions from 'vcomponents/email/listings/EmailListingActions.vue';

import { createNamespacedHelpers } from 'vuex';
const { mapState: corpMapState } = createNamespacedHelpers('corp');

export default {
    components: {
        EmailListingActions,
    },
    mixins: [EmailsListingMixins],
    props: {
        columns: {
            type: Array,
            required: true,
        },
        email: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            EMAIL_STATUS: Object.freeze({
                SENDING_IN_PROGRESS: {
                    code: 'pending_sending',
                    label: this.$t.__('email.EmailListing.sending_in_progress'),
                    tagColor: 'yellow',
                },
                SENDING_FAILED: {
                    code: 'failed_to_send',
                    label: this.$t.__('email.EmailListing.sending_failed'),
                    tagColor: 'red',
                },
                SENT: {
                    code: 'successfully_sent',
                    label: this.$t.__('email.EmailListing.tag_sent'),
                    tagColor: 'blue',
                },
                WAITING_TO_BE_SENT: {
                    code: 'waiting_to_be_sent',
                    label: this.$t.__('email.EmailListing.waiting_to_be_sent'),
                    tagColor: 'yellow',
                },
                OPEN: {
                    code: 'open',
                    label: this.$t.__('email.EmailListing.tag_open'),
                    tagColor: 'green',
                },
            }),
        };
    },
    computed: {
        ...corpMapState(['timeformat', 'hourformat']),
        formatDisplayedDate() {
            return this.timeformat === 'dmy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        },
        formatDisplayedTime() {
            return this.hourformat === '12' ? 'hh:mm a' : 'HH:mm';
        },
        formattedParticipants() {
            return this.email.participants
                .map((participant) =>
                    participant.name && participant.name !== ''
                        ? participant.name
                        : participant.email,
                )
                .join(', ');
        },
        tableRowClass() {
            return [
                'email-listing-table__row',
                {
                    'email-listing-table__row--unread': this.isReceivedTable && this.email.unread,
                },
            ];
        },
        checkboxValues() {
            if (this.email.provider_thread_id) {
                return {
                    value: this.email.provider_thread_id,
                    dataBot: `email-listing-checkbox__${this.email.provider_thread_id}`,
                };
            }

            return {
                value: `${this.email.type}_${this.email.id}`,
                dataBot: `email-listing-checkbox__${this.email.id}`,
            };
        },
    },
    methods: {
        formattedDate(dateToFormat) {
            const date = moment(dateToFormat);

            if (!date.isValid()) {
                return '-';
            }

            const today = moment().startOf('day');
            const diffInDays = date.diff(today, 'days');
            const isSameDay = date.isSame(today, 'day');

            if (diffInDays === 0 && isSameDay) {
                return `${this.$t.__('common.labels.today')}, ${date.format('HH:mm')}`;
            } else if (diffInDays === 1) {
                return `${this.$t.__('common.labels.tomorrow')}, ${date.format('HH:mm')}`;
            }

            return date.format(`${this.formatDisplayedDate}, ${this.formatDisplayedTime}`);
        },
        getStatusLabel() {
            if (this.email.is_open && this.email.status === this.EMAIL_STATUS.SENT.code) {
                return this.EMAIL_STATUS.OPEN.label;
            }

            switch (this.email.status) {
                case this.EMAIL_STATUS.SENDING_IN_PROGRESS.code:
                    return this.EMAIL_STATUS.SENDING_IN_PROGRESS.label;
                case this.EMAIL_STATUS.SENDING_FAILED.code:
                    return this.EMAIL_STATUS.SENDING_FAILED.label;
                case this.EMAIL_STATUS.SENT.code:
                    return this.EMAIL_STATUS.SENT.label;
                case this.EMAIL_STATUS.WAITING_TO_BE_SENT.code:
                    return this.EMAIL_STATUS.WAITING_TO_BE_SENT.label;
                default:
                    return null;
            }
        },
        getStatusColorTag() {
            if (this.email.is_open && this.email.status === this.EMAIL_STATUS.SENT.code) {
                return this.EMAIL_STATUS.OPEN.tagColor;
            }

            switch (this.email.status) {
                case this.EMAIL_STATUS.SENDING_IN_PROGRESS.code:
                    return this.EMAIL_STATUS.SENDING_IN_PROGRESS.tagColor;
                case this.EMAIL_STATUS.SENDING_FAILED.code:
                    return this.EMAIL_STATUS.SENDING_FAILED.tagColor;
                case this.EMAIL_STATUS.SENT.code:
                    return this.EMAIL_STATUS.SENT.tagColor;
                case this.EMAIL_STATUS.WAITING_TO_BE_SENT.code:
                    return this.EMAIL_STATUS.WAITING_TO_BE_SENT.tagColor;
                default:
                    return null;
            }
        },
        updateReadingStatus(updateReadingStatusObject) {
            this.$emit('update:readingStatus', updateReadingStatusObject);
        },
        openRelated(emailToUpdateRelated) {
            this.$emit('open:related', emailToUpdateRelated);
        },
    },
};
</script>
<style lang="scss" scoped>
.email-listing-table {
    &__row {
        cursor: pointer;
        &--unread {
            .email-listing-table__state-dot {
                background-color: var(--colors-component-background-information-dot-primary);
            }

            .email-listing-table__cell-participants-label,
            .email-listing-table__cell-subject,
            .email-listing-table__cell-date {
                font-weight: var(--font-weights-proxima-nova-2) !important;
            }
        }
    }

    &__cell {
        &-participants {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            flex-wrap: nowrap;
            gap: var(--spacing-1);
            overflow: hidden;

            &-label {
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }
        }

        &-subject {
            &-container {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                flex-wrap: nowrap;
                gap: var(--spacing-1);
                overflow: hidden;
            }

            &-content {
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            &-attachment {
                flex-shrink: 0;
            }
        }
    }

    &__state-dot {
        width: var(--spacing-1);
        height: var(--spacing-1);
        flex-shrink: 0;
        border-radius: 50%;
        background-color: var(--colors-sellsy-grayscale-05);
    }
}

.capitalized {
    text-transform: capitalize;
}
</style>
